import React, { useCallback, useEffect, useRef, useState } from 'react';
import { errorText } from '../../pages/SoftwarePolicy/policyEdit';
import {
  LIVE_TONES, RUN_NOW_URL, SCRIPT_TITLES, SCRIPT_TONES, SYNC_TEXT, SYNC_TONES, describeCron, describeDelay, filterText, liveUrl, scheduleUrl, when,
} from './deviceData';
import ResidentDialog from './ResidentDialog';

const list = (v) => (Array.isArray(v) ? v : []);

// Everything about the resident scripts of this device in one place, as the queue site's Schedules, Results and Logs pages: the scripts it is scheduled
// to run and when, the latest value each stored, what the device says about each one now, its log, and Run now. The schedule and the stored results come
// from VES's store, so they show while the device is offline; the live columns and the logs need the device online and are asked for when the tab opens
// or on Refresh, never on a timer (each is a real round trip to the machine).
export default function ResidentsTab({ account, deviceType, deviceId, request }) {
  const requestRef = useRef(request);
  requestRef.current = request;
  const [schedule, setSchedule] = useState({ state: 'loading', summary: null, scripts: [] });
  const [live, setLive] = useState({ state: 'loading', items: [], message: '' });
  const [asOf, setAsOf] = useState('');
  const [showGone, setShowGone] = useState(false);
  const [open, setOpen] = useState(null);                     // {script, startOn}
  const [running, setRunning] = useState('');                 // the name being started
  const [started, setStarted] = useState(null);               // what Run now answered, or its error

  const loadSchedule = useCallback(async () => {
    setSchedule((s) => ({ ...s, state: 'loading' }));
    try {
      const r = await requestRef.current('GET', scheduleUrl(account, deviceType, deviceId));
      setSchedule({ state: 'ok', summary: r.data?.summary || null, scripts: list(r.data?.scripts) });
    } catch (ex) {
      setSchedule({ state: 'error', error: errorText(ex), summary: null, scripts: [] });
    }
  }, [account, deviceType, deviceId]);

  const loadLive = useCallback(async () => {
    setLive((l) => ({ ...l, state: 'loading' }));
    try {
      const r = await requestRef.current('GET', liveUrl(account, deviceType, deviceId));
      setLive(r.data?.online === false ? { state: 'offline', items: [], message: r.data.message || '' } : { state: 'ok', items: list(r.data?.items), message: '' });
    } catch (ex) {
      setLive({ state: 'error', items: [], message: errorText(ex) });
    }
  }, [account, deviceType, deviceId]);

  const refresh = useCallback(() => { setAsOf(when(new Date().toISOString())); loadSchedule(); loadLive(); }, [loadSchedule, loadLive]);
  useEffect(() => { refresh(); }, [refresh]);

  const runNow = async (script) => {
    setRunning(script.name);
    setStarted(null);
    try {
      const r = await requestRef.current('POST', RUN_NOW_URL, { account, deviceType, deviceId, schedule: script.schedule, row: script.row });
      setStarted({ row: script.row, ...r.data });
    } catch (ex) {
      setStarted({ row: script.row, error: errorText(ex) });
    } finally {
      setRunning('');
    }
  };

  const liveBy = new Map(live.items.map((i) => [i.name, i]));
  const scheduled = new Set(schedule.scripts.map((s) => s.name));
  const gone = live.items.filter((i) => i.state === 'not scheduled' && !scheduled.has(i.name));
  const summary = schedule.summary;

  return (
    <section aria-label="Residents">
      <div className="v2-bar tight">
        <h2 className="v2-h2">Residents</h2>
        {summary && (
          <>
            <span className="v2-sub" role="status">{`${summary.schedules} schedule${summary.schedules === 1 ? '' : 's'} · ${summary.active} of ${summary.scripts} script${summary.scripts === 1 ? '' : 's'} active`}</span>
            <span className={`v2-pill ${SYNC_TONES[summary.sync] || 'none'}`} title={`Wanted ${summary.wantedRev || '—'} · applied ${summary.appliedRev || '—'}`}>{SYNC_TEXT[summary.sync] || summary.sync}</span>
          </>
        )}
        <span className="grow" />
        {asOf && <span className="v2-sub">{`as of ${asOf}`}</span>}
        <button type="button" className="v2-btn" onClick={refresh}>Refresh</button>
      </div>
      <p className="v2-muted">
        The scripts this device runs on a schedule. The last and next run and the logs come live from the device, so it has to be online; the results are the
        latest value each script sent (the agent sends it when it changes and at least once an hour).
      </p>

      {schedule.state === 'error' && <div className="v2-alert err" role="alert">{schedule.error}</div>}
      {summary?.errors?.length > 0 && (
        <div className="v2-alert info" role="status">
          {summary.errors.map((e) => <div key={e.name}>{`${e.name}: ${e.message}`}</div>)}
        </div>
      )}
      {live.state === 'offline' && <div className="v2-alert info" role="status">{`${live.message} The schedule and stored results below are still current; the live columns and logs need the device online.`}</div>}
      {live.state === 'error' && <div className="v2-alert err" role="alert">{live.message}</div>}
      {started?.error && <div className="v2-alert err" role="alert">{started.error}</div>}
      {started && !started.error && (
        <div className={`v2-alert${started.delivered && started.ok ? '' : ' info'}`} role="status">
          {started.delivered ? `${started.row}: ${started.message || 'started'}` : `${started.row}: ${started.message || 'The device could not be reached. Nothing was started.'}`}
          {started.delivered && started.ok && <div className="v2-sub">Use Refresh in a few seconds to see the new value.</div>}
        </div>
      )}

      {schedule.state === 'loading' && schedule.scripts.length === 0 && <div className="v2-loading"><span className="v2-spin" role="status" aria-label="Loading" /></div>}
      {schedule.state !== 'loading' && schedule.state !== 'error' && schedule.scripts.length === 0 && (
        <div className="v2-empty">This device is not set to run any resident scripts. Give an endpoint schedule a filter this device matches.</div>
      )}

      {(schedule.scripts.length > 0 || (showGone && gone.length > 0)) && (
        <table className="v2-table" aria-label="Resident scripts">
          <thead><tr><th>Status</th><th>Script</th><th>Runs</th><th>Last run</th><th>Next run</th><th aria-label="Actions" /></tr></thead>
          <tbody>
            {schedule.scripts.map((s) => {
              const l = liveBy.get(s.name);
              const lastRun = l?.lastRunUtc || s.result?.startedUtc;
              return (
                <tr key={s.name}>
                  <td>
                    <span className={`v2-pill ${SCRIPT_TONES[s.status] || 'none'}`} title={s.message || SCRIPT_TITLES[s.status]}>{s.status}</span>
                    {l?.state && s.status !== 'paused' && <span className={`v2-pill ${LIVE_TONES[l.state] || 'none'}`}>{l.state}</span>}
                  </td>
                  <td>
                    <code data-testid="script-name">{s.row}</code>
                    <div className="v2-sub" style={{ wordBreak: 'break-all' }}>{s.functionPath}</div>
                    <div>{filterText(s.filter).map((t) => <span className="v2-chip" key={t}>{t}</span>)}</div>
                  </td>
                  <td>
                    <code>{s.cron}</code>
                    <div className="v2-sub">{describeCron(s.cron) || '—'}</div>
                    {s.maxDelaySeconds ? <div className="v2-sub">{`+ up to ${describeDelay(s.maxDelaySeconds)} random delay`}</div> : null}
                  </td>
                  <td>
                    {lastRun ? when(lastRun) : '—'}
                    {l && l.lastExitCode !== undefined && l.lastExitCode !== null && <div><span className={`v2-pill ${l.lastExitCode === 0 ? 'ok' : 'err'}`}>{`Exit ${l.lastExitCode}`}</span></div>}
                    {!l && s.result?.status && <div><span className={`v2-pill ${s.result.status === 'ok' ? 'ok' : 'err'}`}>{s.result.status}</span></div>}
                    {l && <div className="v2-sub">{`${l.runs ?? 0} run(s), ${l.skipped ?? 0} skipped`}</div>}
                  </td>
                  <td>{l?.nextRunUtc ? when(l.nextRunUtc) : '—'}</td>
                  <td className="nowrap">
                    <button type="button" className="v2-btn" aria-label={`Details of ${s.row}`} onClick={() => setOpen({ script: s, startOn: 'result' })}>Details</button>
                    <button type="button" className="v2-btn" aria-label={`Run ${s.row} now`} disabled={!s.active || Boolean(running)}
                      title={s.active ? 'Start this script on this device now, instead of waiting for its schedule. The device must be online.' : 'This script is paused in its schedule.'}
                      onClick={() => runNow(s)}>{running === s.name ? 'Starting...' : 'Run now'}</button>
                  </td>
                </tr>
              );
            })}
            {showGone && gone.map((g) => (
              <tr key={g.name}>
                <td><span className="v2-pill none">not scheduled</span></td>
                <td colSpan={4}><code>{g.name}</code><div className="v2-sub">No longer in any schedule on this device; only its log is left.</div></td>
                <td className="nowrap"><button type="button" className="v2-btn" aria-label={`Details of ${g.name}`} onClick={() => setOpen({ script: { name: g.name, row: g.name }, startOn: 'log' })}>Details</button></td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {gone.length > 0 && (
        <div className="v2-bar tight">
          <span className="v2-sub">{`${gone.length} script${gone.length === 1 ? '' : 's'} no longer in any schedule on this device (${showGone ? 'shown' : 'hidden'}); ${gone.length === 1 ? 'only its log is' : 'only their logs are'} left.`}</span>
          <button type="button" className="v2-btn" onClick={() => setShowGone((v) => !v)}>{showGone ? 'Hide' : 'Show'}</button>
        </div>
      )}

      {open && <ResidentDialog account={account} deviceType={deviceType} deviceId={deviceId} script={open.script} startOn={open.startOn} request={request} onClose={() => setOpen(null)} />}
    </section>
  );
}
